import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, Calendar, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SocialShare } from '@/components/SocialShare';
import { SEO } from '@/components/SEO';

export default function ScienceOfStillness() {
  const navigate = useNavigate();
  
  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <>
      <SEO 
        title="The Science of Stillness — How Nature Restores the Mind"
        description="Discover how Attention Restoration Theory explains why nature heals our exhausted minds and restores our ability to focus in an overstimulated world."
        keywords={["attention restoration theory", "nature therapy", "mental restoration", "forest bathing", "mindfulness", "nature psychology"]}
        canonicalUrl="https://mysticforest.app/blog/science-of-stillness"
        ogType="article"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <article className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Button
              variant="ghost"
              onClick={handleGoBack}
              className="mb-8 hover:bg-primary/5"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>

            {/* Article Header */}
            <header className="mb-12">
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <time dateTime="2025-10-18">October 18, 2025</time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>7 min read</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>By Piotr</span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-serif font-light mb-8 text-foreground leading-tight">
                The Science of Stillness — How Nature Restores the Mind
              </h1>

              <div className="relative aspect-video w-full overflow-hidden rounded-lg mb-4">
                <img 
                  src="/lovable-uploads/nature-stillness-restoration.jpg"
                  alt="Person resting peacefully in nature by water with wildflowers"
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              <p className="text-sm text-muted-foreground italic text-center">
                Photo: Stock Cake
              </p>
            </header>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "In the woods, we return to reason and faith."
                <footer className="text-sm mt-2">— Ralph Waldo Emerson, <em>Nature</em> (1836), p. 15</footer>
              </blockquote>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Modern Attention Crisis</h2>
              
              <p className="mb-6 leading-relaxed">
                Our attention — once one of our most human qualities — has become a rare resource.
                We scroll while we eat, work while we rest, and multitask our way through moments that were once sacred.
              </p>

              <p className="mb-6 leading-relaxed">
                Psychologists call this state <em>directed attention fatigue</em> — the exhaustion that comes from constantly forcing our minds to focus amid distraction.
                In this overstimulated world, even rest has become another task.
              </p>

              <p className="mb-6 leading-relaxed">
                But long before the concept of "digital detox" entered our vocabulary, a quiet theory began to explain what our nervous systems already knew:
                that nature heals our attention — not through stimulation, but through softness.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Roots of Attention Restoration Theory (ART)</h2>

              <p className="mb-6 leading-relaxed">
                In the 1980s, environmental psychologists Rachel and Stephen Kaplan at the University of Michigan developed the Attention Restoration Theory (ART) — a framework that describes how natural environments help replenish our depleted mental energy.
              </p>

              <p className="mb-6 leading-relaxed">
                They observed that the brain has two types of attention:
              </p>

              <ul className="space-y-4 mb-6 list-disc pl-6">
                <li className="leading-relaxed">
                  <strong>Directed attention</strong> — effortful, controlled, used for work, problem-solving, and resisting distractions.
                </li>
                <li className="leading-relaxed">
                  <strong>Involuntary attention</strong> — gentle, effortless focus that arises when something quietly fascinates us — the sound of wind in leaves, the movement of water, the flicker of firelight.
                </li>
              </ul>

              <p className="mb-6 leading-relaxed">
                When we spend time in nature, we engage the second type.
                The mind stops fighting for control and begins to rest in awareness.
              </p>

              <p className="mb-6 leading-relaxed">
                As Kaplan wrote in <em>The Experience of Nature</em> (New York: Cambridge University Press, 1989, p. 192):
              </p>

              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "Nature engages the mind effortlessly, allowing directed attention to replenish."
              </blockquote>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Why Nature Works</h2>

              <p className="mb-6 leading-relaxed">
                The Kaplans identified four key ingredients of restorative environments:
              </p>

              <ul className="space-y-4 mb-6 list-disc pl-6">
                <li className="leading-relaxed">
                  <strong>Being away</strong> — physical or psychological distance from the demands of everyday life.
                </li>
                <li className="leading-relaxed">
                  <strong>Extent</strong> — a sense of connectedness and immersion in an environment larger than oneself.
                </li>
                <li className="leading-relaxed">
                  <strong>Soft fascination</strong> — stimuli that gently hold attention without demanding it (rippling water, birdsong, forest light).
                </li>
                <li className="leading-relaxed">
                  <strong>Compatibility</strong> — alignment between what the environment offers and what we need — peace, clarity, meaning.
                </li>
              </ul>

              <p className="mb-6 leading-relaxed">
                In other words, the forest restores not by distracting us, but by giving us permission to stop trying so hard.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Modern Science Meets Ancient Wisdom</h2>

              <p className="mb-6 leading-relaxed">
                More than 30 years after Kaplan and Kaplan, neuroscience continues to validate their work.
                A 2019 study in <em>Frontiers in Psychology</em> found that just 20 minutes in nature significantly lowers cortisol levels and restores attentional capacity (Hunter et al., 2019, <em>Frontiers in Psychology</em>, 10:722).
              </p>

              <p className="mb-6 leading-relaxed">
                Another study at Stanford University (Berman et al., <em>Journal of Affective Disorders</em>, 2012) showed that participants who walked in natural environments demonstrated improved memory, mood, and focus compared to those walking in urban areas.
              </p>

              <p className="mb-6 leading-relaxed">
                But beyond the data, the message remains simple:
                the mind is not designed to be constantly online.
              </p>

              <p className="mb-6 leading-relaxed">
                It longs for rhythm, repetition, silence —<br />
                for something that doesn't demand, but allows.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Mystic Forest Way</h2>

              <p className="mb-6 leading-relaxed">
                At Mystic Forest, we see the forest not just as scenery, but as medicine.
                It's where the modern nervous system can remember how to breathe again — without performance, without measurement.
              </p>

              <p className="mb-6 leading-relaxed">
                When we walk among trees, our attention shifts from control to connection.
                We no longer manage time; we inhabit it.
              </p>

              <p className="mb-6 leading-relaxed">
                Our retreats and workshops draw directly from the spirit of ART: slowing down, observing, moving, and resting in gentle fascination.
                Because healing the mind doesn't always require therapy or technology — sometimes, it just requires a path through the woods.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">In the End</h2>

              <p className="mb-6 leading-relaxed">
                We don't go to nature to escape the world.<br />
                We go to remember how to be part of it —<br />
                how to rest our minds,<br />
                and let our attention find its natural rhythm again.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h3 className="text-2xl font-serif font-light mt-12 mb-6">References</h3>

              <ul className="space-y-2 mb-6 text-sm">
                <li>Kaplan, R., & Kaplan, S. (1989). <em>The Experience of Nature: A Psychological Perspective</em>. New York: Cambridge University Press.</li>
                <li>Hunter, M. R., Gillespie, B. W., & Chen, S. Y. (2019). "Urban nature experiences reduce stress in the context of daily life." <em>Frontiers in Psychology</em>, 10:722.</li>
                <li>Berman, M. G., Jonides, J., & Kaplan, S. (2012). "The cognitive benefits of interacting with nature." <em>Journal of Affective Disorders</em>, 140(3), 300–305.</li>
                <li>Emerson, R. W. (1836). <em>Nature</em>. Boston: James Munroe and Company.</li>
              </ul>

              <p className="text-center text-lg italic mt-12 text-muted-foreground">
                We don't go to nature to escape the world. We go to remember how to be part of it.
              </p>
            </div>

            {/* Social Share */}
            <div className="mt-12 pt-8 border-t border-border">
              <SocialShare 
                url="https://mysticforest.app/blog/science-of-stillness"
                title="The Science of Stillness — How Nature Restores the Mind"
                description="Discover how Attention Restoration Theory explains why nature heals our exhausted minds."
              />
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}
